import React, { useContext, useEffect, CSSProperties } from 'react';
import useStyles from 'pages/MarketingPage/PreSave/styles';
import RecordPreview from 'components/shareable/RecordPreview';
import { BackgroundTypes, TitleAlignments, PresaveWebsiteStyle } from 'modules/Types';
import { PreSaveDataContext } from 'pages/MarketingPage/Data/PreSaveProvider';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import {
  getRecordPreviewProps,
  getRecordPreviewContainerStyles,
} from '../utils';
import { PresaveStep } from 'pages/MarketingPage/PreSave/PreSave';
import upperFirst from 'lodash/upperFirst';
import SuccessInfoLabel from 'components/shareable/SuccessInfoLabel';
import { scrollIntoView } from 'helpers/StyleUtils';
import Divider from 'elements/Divider';
import CardSymphony from 'elements/CardSymphony';
import Sty from 'modules/Sty';
import { clsx } from 'modules/Utils';

/** Props for the MusicPlatforms component */
interface MusicPlatformsProps {
  platforms: { url?: string; name: string; show: boolean }[];
}

/**
 * Component to display music platforms as a list
 */
const MusicPlatforms = ({
  platforms,
}: MusicPlatformsProps) => {
  return (
    <React.Fragment>
      {platforms.map((p, idx) => {
        const { name, show } = p;
        const isSpotify = isEqual(name, 'spotify') && show;
        const isDeezer = isEqual(name, 'deezer') && show;
        const isAppleMusic = isEqual(name, 'apple_music') && show;
        const isAudiomack = isEqual(name, 'audiomack') && show;
        const isSoundcloud = isEqual(name, 'soundcloud') && show;

        if (isSpotify) return <div key={`${name}-${idx}`}>Spotify</div>;
        if (isDeezer) return <div key={`${name}-${idx}`}>Deezer</div>;
        if (isAppleMusic) return <div key={`${name}-${idx}`}>Apple Music</div>;
        if (isAudiomack) return <div key={`${name}-${idx}`}>Audiomack</div>;
        if (isSoundcloud) return <div key={`${name}-${idx}`}>Soundcloud</div>;
        return <React.Fragment key={`fragment-${idx}`} />;
      })}
    </React.Fragment>
  );
};

/** Props for the ReviewTab component */
interface ReviewTabProps {
  website: PresaveWebsiteStyle;
  brandArtwork: string | null;
  contentName: string | null;
  selectedTab: PresaveStep;
}

/**
 * The ReviewTab component provides a summary of the presave configuration details
 */
const ReviewTab = ({
  website,
  brandArtwork,
  selectedTab,
}: ReviewTabProps) => {
  //TODO: Uncomment when adding url again
  // const content = contentName || undefined;
  const { details, customize } = useContext(PreSaveDataContext);
  const {
    releaseDate,
    musicPlatforms,
    timezone,
    boosts: { collectEmails, boostFollows, autoPresaves }
  } = details;
  const abbrev = timezone.abbrev ? `[${timezone.abbrev}]` : '';
  const {
    releaseTitle,
    artwork: artworkUrl,
    subtitle
  } = customize;
  const classes = useStyles();

  /** Array of all boost options */
  const boosts = [collectEmails, boostFollows, autoPresaves];

  /** Whether any boosts are enabled */
  const showBoosts = boosts.filter((b) => b).length > 0;

  // const formattedContent = formatValueAsUrl(content);
  // const formattedReleaseTitle = formatValueAsUrl(releaseTitle);
  // const link = `https://symphony.to/${formattedContent}/${formattedReleaseTitle}`;

  const recordPreviewProps = getRecordPreviewProps({
    brandArtwork,
    releaseTitle,
    subtitle,
    titleAlignment: TitleAlignments.CENTER,
    positionsOptions: musicPlatforms.filter((p) => p.show),
    customize,
    website,
  });

  const { style: containerStyle, className: containerClassName } =
    getRecordPreviewContainerStyles({
      classes,
      backgroundColor: recordPreviewProps.backgroundColor,
      backgroundType: recordPreviewProps.backgroundType,
    });

  useEffect(() => {
    scrollIntoView();
  }, [selectedTab]);

  return (
    <div className="reviewTabComponent" style={sty.reviewTabComponent}>
      <div className="reviewTabDataColumn" style={sty.reviewTabDataColumn}>
        <CardSymphony
          className="reviewTabHeader"
          title="Let's launch your pre-save"
          subtitle="Confirm all the details look right before we go-live."
          size="small"
        >
          <div className="headerSection" style={sty.headerSection}>
            <div className="numberBlock" style={sty.numberBlock}>01</div>
            <div>Pre-save Details</div>
          </div>
        </CardSymphony>

        <Divider />

        <CardSymphony
          className="reviewTabHeader"
          title={
            <div className="artworkTitleSection" style={sty.artworkTitleSection}>
              <img
                className={classes.artworkPreview}
                alt="artwork"
                src={artworkUrl || ''}
                style={sty.artwork}
              />
              <span>{upperFirst(releaseTitle)}</span>
            </div>
          }
          size="small"
        />

        <Divider />

        <CardSymphony
          className="releaseDateCard"
          style={sty.dataPanelRow}
        >
          <div style={Sty.text18bold}>
            Release Date
          </div>
          <div style={Sty.text16grey}>
            {moment(releaseDate).format(`MMMM D, YYYY h:mm a ${abbrev}`)}
          </div>
        </CardSymphony>

        <Divider />

        <CardSymphony
          className="enabledPlatformsCard"
          style={sty.dataPanelRow}
        >
          <div style={Sty.text18bold}>
            Enabled Platforms
          </div>
          <div className="platformsList" style={sty.platformsList}>
            <MusicPlatforms platforms={musicPlatforms} />
          </div>
        </CardSymphony>

        <Divider />

        <CardSymphony className="configurationsCard">
          <div className="headerSection" style={sty.headerSection}>
            <div className="numberBlock" style={sty.numberBlock}>02</div>
            <div>Configurations</div>
          </div>
        </CardSymphony>

        <Divider />

        <CardSymphony
          className="enabledBoostsCard"
          style={sty.dataPanelRow}
        >
          <div style={Sty.text18bold}>
            Enabled Boosts
          </div>

          {showBoosts ? (
            <div className="boostsList" style={sty.boostsList}>
              {collectEmails && <div>Collect Fan Data</div>}
              {boostFollows && <div>Boost Follows</div>}
              {autoPresaves && <div>Forever Saves</div>}
            </div>
          ) : (
            <div>None</div>
          )}
        </CardSymphony>

        <Divider />

        <CardSymphony className="reviewTabCard">
          <SuccessInfoLabel
            label="Once your song releases, the pre-save will automatically convert into a landing page with all of your song's streaming links."
          />
        </CardSymphony>
      </div>

      {/* <div className={clsx('rightSidePresavePreviewColumn', classes.previewColumn)}>
        <div className={containerClassName} style={containerStyle}>
          {recordPreviewProps.backgroundType === BackgroundTypes.ARTWORK &&
            <img
              className="filter blur-lg absolute w-auto object-cover h-full"
              src={recordPreviewProps.contentDetails.artworkUrl}
              alt="artwork_as_background"
            />
          }
          <RecordPreview
            {...recordPreviewProps}
            className={classes.principalPreviewContainer}
          />
        </div>
      </div> */}
    </div>
  );
};

/** Component styles */
const sty: Record<string, CSSProperties> = {
  reviewTabComponent: {
    display: 'flex',
    gap: 16,
  },
  reviewTabDataColumn: {
    backgroundColor: 'white',
    borderRadius: 8,
    overflow: 'hidden',
  },
  headerSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  numberBlock: {
    background: '#f0eaf9',
    color: '#6c48c5',
    fontWeight: 600,
    borderRadius: 4,
    padding: '4px 8px',
  },
  artworkTitleSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  artwork: {
    borderRadius: 8,
  },
  dataPanelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  platformsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  boostsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
};

export default ReviewTab;
